import React, { useRef, useState } from 'react';
import { FaFileUpload, FaCheckCircle } from 'react-icons/fa';
import PageHero from '../components/PageHero';
import FormField, { inputClass, Honeypot, FormStatus, SubmitButton } from '../components/forms/FormField';
import { POSITIONS, validateCareer, validateCv } from '../lib/formValidation';
import { submitForm, bytesToBase64 } from '../lib/submitForm';

const emptyForm = { name: '', email: '', phone: '', position: '', age: '', website: '' };
const fieldOrder = ['name', 'email', 'phone', 'position', 'age', 'cv'];

const CareersPage = () => {
    const [form, setForm] = useState(emptyForm);
    const [cv, setCv] = useState(null); // { name, size, bytes }
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');
    const fileInput = useRef(null);

    const cvMeta = cv ? { name: cv.name, size: cv.size } : null;
    const fieldError = (name, values) => validateCareer({ ...values, cv: cvMeta }, cv?.bytes).errors[name];

    const handleChange = (e) => {
        const { name, value } = e.target;
        const next = { ...form, [name]: value };
        setForm(next);
        if (touched[name]) setErrors((prev) => ({ ...prev, [name]: fieldError(name, next) }));
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
        setErrors((prev) => ({ ...prev, [name]: fieldError(name, form) }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        setTouched((prev) => ({ ...prev, cv: true }));
        if (!file) {
            setCv(null);
            setErrors((prev) => ({ ...prev, cv: 'Please attach your CV.' }));
            return;
        }
        // Reject on size before reading a huge file into memory.
        const sizeError = validateCv({ name: file.name, size: file.size });
        if (sizeError) {
            setCv(null);
            setErrors((prev) => ({ ...prev, cv: sizeError }));
            return;
        }
        const bytes = new Uint8Array(await file.arrayBuffer());
        const contentError = validateCv({ name: file.name, size: file.size }, bytes);
        if (contentError) {
            setCv(null);
            setErrors((prev) => ({ ...prev, cv: contentError }));
            return;
        }
        setCv({ name: file.name, size: file.size, bytes });
        setErrors((prev) => ({ ...prev, cv: undefined }));
    };

    const focusFirstError = (errs) => {
        const first = fieldOrder.find((f) => errs[f]);
        if (first) document.getElementById(`career-${first}`)?.focus();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === 'submitting') return;

        const { errors: errs, valid } = validateCareer({ ...form, cv: cvMeta }, cv?.bytes);
        // Keep the specific reason a chosen file was rejected rather than "Please attach your CV."
        if (!cv && errors.cv) errs.cv = errors.cv;
        setTouched(Object.fromEntries(fieldOrder.map((f) => [f, true])));
        setErrors(errs);
        if (!valid) {
            setStatus('error');
            setMessage('Please fix the highlighted fields.');
            focusFirstError(errs);
            return;
        }

        setStatus('submitting');
        setMessage('Uploading your application…');
        const result = await submitForm('/api/careers', {
            ...form,
            cv: { name: cv.name, data: bytesToBase64(cv.bytes) },
        }, { timeoutMs: 60000 });

        if (result.ok) {
            setForm(emptyForm);
            setCv(null);
            if (fileInput.current) fileInput.current.value = '';
            setTouched({});
            setErrors({});
            setStatus('success');
            setMessage("Thank you! Your application has been received. We'll contact you if your profile matches an opening.");
        } else {
            setErrors(result.fieldErrors);
            setStatus('error');
            setMessage(result.error);
            focusFirstError(result.fieldErrors);
        }
    };

    const control = (name, extra = {}) => ({
        id: `career-${name}`,
        name,
        value: form[name],
        onChange: handleChange,
        onBlur: handleBlur,
        'aria-invalid': errors[name] ? true : undefined,
        'aria-describedby': `career-${name}-error`,
        className: inputClass(Boolean(errors[name])),
        ...extra,
    });

    return (
        <>
            <PageHero title="Careers" />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
                    {/* Intro */}
                    <div className="lg:col-span-2 bg-brand-navy text-white rounded-3xl p-8 sm:p-10 shadow-xl relative overflow-hidden">
                        <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-brand-yellow/20 blur-2xl" aria-hidden="true" />
                        <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-brand-yellow">
                            Career Opportunities
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight">Join Our Team</h2>
                        <div className="mt-5 h-1 w-14 rounded-full bg-brand-yellow" />
                        <p className="mt-5 text-blue-100 leading-relaxed">
                            We're always looking for talented individuals to join our team. Fill out the form below to apply.
                        </p>
                        <ul className="mt-8 space-y-3 relative">
                            {POSITIONS.map((pos) => (
                                <li key={pos} className="flex items-center gap-3 text-sm sm:text-base">
                                    <FaCheckCircle className="text-brand-yellow shrink-0" aria-hidden="true" />
                                    {pos}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3 bg-white p-6 sm:p-8 lg:p-10 rounded-3xl shadow-xl border border-gray-100">
                        <form onSubmit={handleSubmit} noValidate className="relative">
                            <Honeypot value={form.website} onChange={handleChange} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1">
                                <FormField id="career-name" label="Name" required error={errors.name}>
                                    <input type="text" autoComplete="name" {...control('name')} />
                                </FormField>
                                <FormField id="career-email" label="Email" required error={errors.email}>
                                    <input type="email" autoComplete="email" {...control('email')} />
                                </FormField>
                                <FormField id="career-phone" label="Phone" required error={errors.phone}>
                                    <input type="tel" autoComplete="tel" inputMode="tel" {...control('phone')} />
                                </FormField>
                                <FormField id="career-age" label="Age" required error={errors.age}>
                                    <input type="number" min="18" max="100" inputMode="numeric" {...control('age')} />
                                </FormField>
                                <div className="sm:col-span-2">
                                    <FormField id="career-position" label="Position" required error={errors.position}>
                                        <select {...control('position')}>
                                            <option value="">Select a position</option>
                                            {POSITIONS.map((pos) => (
                                                <option key={pos} value={pos}>{pos}</option>
                                            ))}
                                        </select>
                                    </FormField>
                                </div>
                                <div className="sm:col-span-2">
                                    <FormField
                                        id="career-cv"
                                        label="Upload CV"
                                        required
                                        error={errors.cv}
                                        hint="PDF, DOC or DOCX, up to 3 MB."
                                    >
                                        <label
                                            htmlFor="career-cv"
                                            className={`flex items-center gap-4 rounded-xl border-2 border-dashed px-4 py-4 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-brand-yellow/60 ${
                                                errors.cv ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-brand-navy hover:bg-gray-50'
                                            }`}
                                        >
                                            <span className="shrink-0 w-11 h-11 rounded-xl bg-brand-navy text-brand-yellow flex items-center justify-center">
                                                <FaFileUpload aria-hidden="true" />
                                            </span>
                                            <span className="min-w-0 text-sm">
                                                <span className="block font-semibold text-brand-navy truncate">
                                                    {cv ? cv.name : 'Choose a file'}
                                                </span>
                                                <span className="block text-gray-500">
                                                    {cv ? `${Math.max(1, Math.round(cv.size / 1024))} KB` : 'Click to browse'}
                                                </span>
                                            </span>
                                            <input
                                                ref={fileInput}
                                                type="file"
                                                id="career-cv"
                                                name="cv"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={handleFileChange}
                                                aria-invalid={errors.cv ? true : undefined}
                                                aria-describedby="career-cv-error"
                                                className="sr-only"
                                            />
                                        </label>
                                    </FormField>
                                </div>
                            </div>

                            <FormStatus status={status} message={message} />
                            <SubmitButton submitting={status === 'submitting'} submittingLabel="Submitting…">
                                Submit Application
                            </SubmitButton>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CareersPage;
